import Router from 'next/router';
import { ofType } from 'redux-observable';
import { Observable, of } from 'rxjs';
import { auditTime, debounceTime, map, mergeMap, withLatestFrom } from 'rxjs/operators';
import { getSupplierUrl } from '@bridebook/toolbox';
import { Gazetteer } from '@bridebook/toolbox/src/gazetteer';
import type { IUISupplier } from '@bridebook/toolbox/src/types';
import { getLocationName } from '@bridebook/web/lib/analytics-utils';
import { getIsCollaborator } from '@bridebook/web/lib/analytics-utils/get-is-collaborator';
import { getScreenType } from '@bridebook/web/lib/app/selectors';
import { WebAnalyticsContext } from '@bridebook/web/lib/bbcommon/utils/bridebook-analytics';
import { env } from '@bridebook/web/lib/env';
import { getCountryCode } from '@bridebook/web/lib/i18n/selectors';
import {
  IChangedSearchCategoryAnalytics,
  IChangedSearchLocationAnalytics,
  IChangedSearchLocationAnalyticsDebounced,
  IClickedOnFilteredSearchTileAnalytics,
  IFetchSearchSuccessAction,
  IFetchSearchSuccessAnalytics,
  ILoadedQuizResultsAnalytics,
  ISearchTriggerClickedSuppliersPageAnalytics,
  ISetSearchLocationAutocompleteText,
  SearchActionTypes,
} from '@bridebook/web/lib/search/action-types';
import {
  changedSearchLocationAnalytics,
  changedSearchLocationAnalyticsDebounced,
  fetchedSearchDataAnalytics,
  mapMarkerClickedAnalytics,
} from '@bridebook/web/lib/search/actions';
import { getIdentityPropsKey } from '@bridebook/web/lib/search/utils/get-identity-props-key';
import mapSupplierListToAnalytics from '@bridebook/web/lib/search/utils/map-suppliers/map-supplier-list-to-analytics';
import type {
  Action,
  IApplicationState,
  IEpicDeps,
  ILoadedSearchResultsEventProps,
  ILoadedSearchResultsPropsFromSearchResponse,
  IResetSearchFiltersEventProps,
  ISearchState,
  IToggledSearchFiltersEventProps,
  IUsedSearchFiltersEventProps,
  IUsedSortByEventProps,
  MobileSearchViewAnalytics,
  TSearchResponse,
} from '@bridebook/web/lib/types';
import { ProtectedUrls } from '@bridebook/web/lib/url-helper';
import {
  IFetchVenuerexSuppliersSuccessAction,
  VenuerexActionTypes,
} from '@bridebook/web/lib/venuerex/action-types';
import { getVenueBooked } from '@bridebook/web/lib/weddings/selectors';
import formatQuizFilters from '../../components/quiz/format-quiz-filters';
import { analyticsCategory } from './analytics-constants';
import { ISearchFiltersPropertiesGeneric, SearchFiltersLocation } from './types';
import { RerankingAnalytics } from '@bridebook/toolbox/src/search-suppliers/reranking/reranking-service';
import { buildViewedSupplierInSearchEvent } from './analytics-builders/get-viewed-supplier-analytics-props';
import { buildAnalyticsActiveFilters } from './analytics-builders/build-active-filters';

const DEFAULT_DEBOUNCE_TIME = 5000;

// do not change strings, it will affect analytics
export enum MapToggleStateAnalytics {
  MapView = 'map',
  ListView = 'list',
}

// do not change string, it will affect analytics
export const presetsSection = 'presetsHeader';

const getSearchBarPropertiesGeneric = (
  getState: () => IApplicationState,
  type: string | boolean = '',
  openState: any = null,
  method: string | null = '',
  activeTab: string = '',
) => {
  const {
    app: {
      previousPath,
      query: { searchPopup },
    },
    ui: { barIsVisible },
    search: {
      request: { slug, area },
      searchModalOptions: { activeTab: activeTabFromState, isVenueSearch },
    },
  } = getState();

  let searchBarCategory = type || slug;

  if ((searchPopup === 'true' || openState !== null) && isVenueSearch) {
    searchBarCategory = 'venue';
  }

  return {
    searchBarToggledStatus: openState === null ? barIsVisible : openState,
    searchBarCategory,
    searchBarSearchTerm: area,
    searchBarToggledMethod: method,
    searchBarSearchType: activeTab || activeTabFromState,
    previousPath,
  };
};

interface ISearchFiltersPropertiesProps {
  name: string;
  value: { [s: string]: number | string };
  tag: string | null;
  location: SearchFiltersLocation;
  getState: () => IApplicationState;
}

/**
 * Outputs analytics searchFiltersPropertiesGeneric.
 */
const searchFiltersPropertiesGeneric = ({
  name,
  value,
  location,
}: ISearchFiltersPropertiesProps): ISearchFiltersPropertiesGeneric => {
  if (name === 'misc') {
    return {
      searchFiltersField: 'range',
      searchFiltersLocation: location || 'filtersModal',
      searchFiltersValue: Object.values(value)[0]?.toString(),
      searchFiltersSection: Object.keys(value)[0]?.toString(),
    };
  }

  return {
    searchFiltersField: Object.entries(value)
      .filter(([, value]) => !!value)
      .map(([key]) => key),
    searchFiltersLocation: location || 'filtersModal',
    searchFiltersValue: true,
    searchFiltersSection: name,
  };
};

export interface ISearchSuccessAnalyticsCustomData {
  inlineAdPresent?: boolean;
}

const getLoadedSearchResultsPropsFromSearchResponse = ({
  searchResponse,
}: {
  searchResponse: TSearchResponse;
}): ILoadedSearchResultsPropsFromSearchResponse => {
  const { filters, fields, results, stats, totalResults: searchResultsTotal } = searchResponse;
  const {
    preset,
    swlat,
    swlon,
    nelat,
    nelon,
    searchType,
    placeId,
    area: searchTerm,
    page: searchPageNumber,
    type: searchSupplierCategory,
    sort: searchSortedBy,
  } = fields;

  const loadedSearchResultsList = results.map(({ id }) => id);

  const loadedSearchResultsCoreScore = results
    .filter((item: IUISupplier) => loadedSearchResultsList.includes(item.id))
    .map(({ _score = 0 }) => _score);

  const searchCoords = { swlat, swlon, nelat, nelon };

  const allActiveFilters = {
    ...buildAnalyticsActiveFilters(filters, fields),
    ...(preset ? { [preset]: { [preset]: true } } : {}),
  };

  const idToStats = (stats?.suppliers || []).reduce(
    (prev, next) => {
      prev[next.id] = next;
      return prev;
    },
    {} as Record<string, RerankingAnalytics['suppliers'][number]>,
  );

  const loadedSearchResultsFlags: ILoadedSearchResultsPropsFromSearchResponse['loadedSearchResultsFlags'] =
    results.map((item: IUISupplier) => {
      const isSupplierBoosted = Boolean(item.supplierBoosted && item.boost && !item.isForeign);
      const topSpotVenueType = Boolean(
        item.searchBoostForType && filters?.venueType?.[item.searchBoostForType] && !item.isForeign,
      );
      const supplierRankingInfo = idToStats[item.id];
      return {
        tier: item.tier || 0,
        registered: Boolean(item.registrationDate),
        foreign: Boolean(item.isForeign),
        positionDelta: supplierRankingInfo?.positionDelta,
        mlScore: supplierRankingInfo?.mlScore,
        finalScore: supplierRankingInfo?.finalScore,
        ...(searchSupplierCategory !== 'venue'
          ? { searchBoost: isSupplierBoosted }
          : {
              topSpot_county: Boolean(isSupplierBoosted),
              topSpot_country: item.matchedBy?.includes('placeTopSpot') || false,
              topSpot_venueType: topSpotVenueType,
            }),
      };
    });

  return {
    placeId,
    searchSupplierCategory,
    searchTerm,
    searchType,
    searchCoords,
    searchActiveFilters: allActiveFilters,
    searchResultsLoaded: results.length,
    searchPageNumber,
    searchResultsTotal,
    searchSortedBy,
    loadedSearchResultsList,
    loadedSearchResultsFlags,
    loadedSearchResultsCoreScore,
    interactionsCount: stats?.interactionsCount,
  };
};

const filterLabelProps = (props: { [k: string]: any }) => {
  const newProps = { ...props };
  delete newProps.loadedSearchResultsList;
  return newProps;
};

/*
 * This epic automatically sends an analytics event when the FETCH_SEARCH_SUCCESS action occurs and a 'track' is set in the action's payload.
 * */
export const sendAnalyticsOnFetchSearchSuccessEpic = (
  action$: Observable<Action>,
  deps: IEpicDeps,
) =>
  action$.pipe(
    ofType(SearchActionTypes.FETCH_SEARCH_SUCCESS),
    withLatestFrom(deps.state$),
    auditTime(1500),
    mergeMap(([action, state]) => {
      const payload = action.payload as IFetchSearchSuccessAction['payload'];
      const { track } = payload;

      return state.users.user && track
        ? of(
            fetchedSearchDataAnalytics({
              ...payload,
            }),
          )
        : of();
    }),
  );

export interface AnalyticsHelpersWindow extends Window {
  _cio?: {
    identify: (args: unknown) => void;
  };
}

declare let window: AnalyticsHelpersWindow;

export const fetchSearchSuggestionsAnalyticsEpic = (
  action$: Observable<Action>,
  { state$ }: IEpicDeps,
) =>
  action$.pipe(
    ofType(SearchActionTypes.FETCH_SEARCH_SUGGESTIONS_ANALYTICS_DEBOUNCED),
    withLatestFrom(state$),
    debounceTime(DEFAULT_DEBOUNCE_TIME),
    map(([{ payload }, state]: [Action, IApplicationState]) => {
      const pathname = state.app.pathname;
      const previousPath = state.app.previousPath;
      return {
        type: SearchActionTypes.FETCH_SEARCH_SUGGESTIONS_ANALYTICS,
        payload: {
          searchTerm: payload,
          previousPath,
          pathname,
        },
      };
    }),
  );

export default function searchAnalytics(
  action: Action,
  bridebookAnalytics: WebAnalyticsContext,
  getState: () => IApplicationState,
) {
  const { track, identifyWithTrack: identify } = bridebookAnalytics.getMethods(
    analyticsCategory,
    filterLabelProps,
  );
  const { payload } = action;
  let eventHandled = true;

  const {
    app: { previousPath },
    search: {
      request: { slug },
    },
  } = getState();

  switch (action.type) {
    case SearchActionTypes.TOGGLE_SEARCH_BAR_ANALYTICS: {
      const { openState, searchBarToggledMethod } = payload;
      track({
        event: 'Toggled search bar',
        ...getSearchBarPropertiesGeneric(getState, false, openState, searchBarToggledMethod),
      });
      break;
    }
    case SearchActionTypes.TOGGLED_SEARCH_TYPE_ANALYTICS: {
      const { activeTab } = payload;
      track({
        event: 'Toggled search type',
        ...getSearchBarPropertiesGeneric(getState, '', null, null, activeTab),
      });
      break;
    }
    case SearchActionTypes.FETCH_SEARCH_SUGGESTIONS_ANALYTICS: {
      const isVenueConfirmationPopup = getState().venueConfirmation.showVenueConfirm;
      track({
        previousPath: payload.previousPath,
        event: 'Performed supplier search by name',
        searchedByNameTerm: payload.searchTerm,
        searchedByNameLocation: isVenueConfirmationPopup
          ? 'bookingConfirmationPopup'
          : payload?.searchedByNameLocation || getLocationName(getState(), payload.pathname),
      });
      break;
    }
    case SearchActionTypes.SEARCH_LOAD_MORE_ANALYTICS: {
      const { pageNumber: searchPageNumber } = payload;
      track({
        event: 'Clicked load more search results',
        searchPageNumber,
      });
      break;
    }

    case SearchActionTypes.FETCH_SEARCH_SUCCESS_ANALYTICS: {
      const state = getState();
      const {
        search: { searchSource, mobileSearchViewAnalytics, onboardingFilters },
        users: { user },
        weddings: { profile: weddingProfile },
      } = state;
      const {
        customData,
        searchResponse: {
          results,
          fields: { countryCode, type, page, area: searchTerm },
        },
        searchResponse,
      } = action.payload as IFetchSearchSuccessAnalytics['payload'];

      const isDesktop = getScreenType(state).isDesktop;

      const searchView = isDesktop ? '' : mobileSearchViewAnalytics;

      // LIVE-21094: Check if the filters in search include the onboardingFilters
      const onboardingFiltersIncluded = Object.keys(onboardingFilters).every((key) => {
        if (searchResponse?.fields && Object.hasOwn(searchResponse.fields || {}, key)) {
          return (
            (searchResponse?.fields as Record<string, any>)[key] ===
            onboardingFilters[key].toString()
          );
        }
        return false;
      });

      const determinedEventData = {
        type,
        countryName: Gazetteer.getCountryName(countryCode),
        countryCode,
        page,
      };

      const trackProps: ILoadedSearchResultsEventProps = {
        event: 'Loaded search results',
        ...getLoadedSearchResultsPropsFromSearchResponse({
          searchResponse,
        }),
        searchView: searchView as MobileSearchViewAnalytics,
        previousPath,
        inlineAdPresent: !!customData?.inlineAdPresent,
        searchSource: searchSource || 'newSearch',
        budgetInitialTarget: weddingProfile.budget,
        onboardingFilters: searchSource === 'finishedOnboarding' && onboardingFiltersIncluded,
        ...(determinedEventData
          ? {
              supplierCountryName: determinedEventData.countryName,
              supplierCountryCode: determinedEventData.countryCode,
            }
          : { supplierCountryName: 'N/A', supplierCountryCode: 'N/A' }),
      };

      if (
        user &&
        determinedEventData &&
        (determinedEventData.type === 'venue' || determinedEventData.type === 'photo')
      ) {
        const identifyProps = {
          [getIdentityPropsKey(determinedEventData.type)]: mapSupplierListToAnalytics(
            results,
            determinedEventData.type,
          ),
          searchTerm,
        };

        identify(identifyProps, trackProps, { userId: user?.id });

        if (typeof window?._cio !== 'undefined') {
          window._cio.identify({
            id: user.id,
            email: user?.contacts?.email,
          });
        }
      } else {
        track(trackProps);
      }
      break;
    }

    case VenuerexActionTypes.FETCH_VENUEREX_SUPPLIERS_SUCCESS: {
      const { supplierType, list } = payload as IFetchVenuerexSuppliersSuccessAction['payload'];

      const identityProps = {
        [getIdentityPropsKey(supplierType)]: mapSupplierListToAnalytics(list, supplierType),
      };

      identify(identityProps, undefined, { userId: getState().users.user?.id });
      break;
    }
    case SearchActionTypes.TOGGLED_SEARCH_FILTERS_ANALYTICS: {
      const {
        search: {
          filtersShown: searchToggledFiltersStatus,
          mobileSearchViewAnalytics: searchToggledFiltersLocation,
        },
      } = getState();

      const { method: searchToggledFiltersMethod } = payload;

      track({
        event: 'Toggled search filters',
        searchToggledFiltersLocation,
        searchToggledFiltersMethod,
        searchToggledFiltersStatus,
        previousPath,
        supplierCategory: slug,
      } as IToggledSearchFiltersEventProps);
      break;
    }

    case SearchActionTypes.UPDATE_SEARCH_FILTERS_ANALYTICS: {
      track({
        event: 'Used search filters',
        ...searchFiltersPropertiesGeneric({ ...payload }),
        previousPath,
        supplierCategory: slug,
      } as IUsedSearchFiltersEventProps);
      break;
    }

    case SearchActionTypes.RESET_SEARCH_FILTERS_ANALYTICS: {
      const {
        search: { mobileSearchViewAnalytics: searchToggledFiltersLocation },
      } = getState();

      track({
        event: 'Reset search filters',
        searchToggledFiltersLocation,
        previousPath,
        supplierCategory: slug,
      } as IResetSearchFiltersEventProps);
      break;
    }

    case SearchActionTypes.SEARCH_SORT_CHANGE_ANALYTICS: {
      const { sortBy: searchSortedBy } = payload;
      track({
        event: 'Used sort by',
        searchSortedBy,
        previousPath,
        supplierCategory: slug,
      } as IUsedSortByEventProps);
      break;
    }

    case SearchActionTypes.MAP_MARKER_CLICKED_ANALYTICS: {
      const { searchView, supplier, hasThumbnail } = payload as ReturnType<
        typeof mapMarkerClickedAnalytics
      >['payload'];
      const { type, id, name, publicId, town, county, seoUrlSlug } = supplier;
      track({
        event: 'User clicked on map pin',
        viewedSupplierCategory: type,
        viewedSupplierId: id,
        viewedSupplierName: name,
        viewedSupplierProfileURL:
          env.COUPLESIDE_URL +
          getSupplierUrl({
            type,
            publicId,
            id,
            name,
            town,
            county,
            seoUrlSlug,
          }),
        hasThumbnail,
        ...(searchView && { searchView }),
      });

      break;
    }

    case SearchActionTypes.STARTED_QUIZ: {
      const { quizName, location, category } = payload;
      const previousPath = getState().app.previousPath;
      const venueBooked = getVenueBooked(getState());
      track({
        event: 'User started quiz',
        quizName,
        location,
        previousPath,
        venueBooked,
        category,
      });
      break;
    }
    case SearchActionTypes.NO_RESULTS_QUIZ: {
      const { quizName, searchSupplierCategory, location } = payload;
      const state = getState();
      const previousPath = state.app.previousPath;
      const collaborators = state.weddings.collaborators;
      const weddingId = state.weddings.profile.id;
      const userEmail = state.users.user?.contacts?.email;
      const countryCode = getCountryCode(state);
      const { isMobile: mobileApp } = getScreenType(state);
      track({
        event: 'Loaded no quiz match page',
        profileType: 'user',
        searchSupplierCategory,
        quizName,
        location,
        previousPath,
        countryCode,
        userEmail,
        weddingId,
        collaborators,
        mobileApp,
      });
      break;
    }
    case SearchActionTypes.CHOSE_QUIZ_FILTERS: {
      const {
        quizName,
        quizStepNumber,
        quizStepContent,
        filters,
        category = analyticsCategory,
      } = payload;
      const { none, ...quizFilters } = formatQuizFilters(filters);
      const previousPath = getState().app.previousPath;

      track({
        event: 'User chose quiz filters',
        quizName,
        quizStepNumber,
        quizStepContent,
        quizFilters,
        previousPath,
        category,
      });
      break;
    }
    case SearchActionTypes.WENT_TO_NEXT_QUIZ_STEP: {
      const { quizName, quizStepNumber, quizStepContent, filters, navigationMethod } = payload;
      const { none, ...quizFilters } = formatQuizFilters(filters);
      const previousPath = getState().app.previousPath;

      track({
        event: 'User went to next quiz page',
        quizName,
        quizStepNumber,
        quizStepContent,
        quizFilters,
        navigationMethod,
        previousPath,
      });
      break;
    }
    case SearchActionTypes.REVEALED_QUIZ_MATCHES: {
      const { quizName, quizResultType, quizResult, filters } = payload;
      const { none, ...quizFilters } = formatQuizFilters(filters);
      const previousPath = getState().app.previousPath;

      track({
        event: 'User revealed quiz matches',
        quizName,
        quizResultType,
        quizResult,
        quizFilters,
        previousPath,
      });
      break;
    }
    case SearchActionTypes.EXITED_QUIZ: {
      const { quizName, quizStepNumber, quizStepContent, category } = payload;
      const previousPath = getState().app.previousPath;

      track({
        event: 'User exited quiz',
        category,
        quizName,
        quizStepNumber,
        quizStepContent,
        previousPath,
      });
      break;
    }
    case SearchActionTypes.LOADED_QUIZ_RESULTS_ANALYTICS: {
      const analyticsPayload = payload as ILoadedQuizResultsAnalytics['payload'];

      track({
        event: 'Loaded quiz results',
        previousPath,
        ...analyticsPayload,
      });
      break;
    }
    case SearchActionTypes.LOADED_SEARCH_PREFERENCE_PAGE: {
      const { searchSupplierCategory } = payload;
      track({
        event: 'Loaded search preference page',
        searchSupplierCategory,
      });
      break;
    }
    case SearchActionTypes.LOADED_RECOMMENDED_SUPPLIER_PAGE: {
      track({
        event: 'Loaded recommended supplier page',
      });
      break;
    }
    case SearchActionTypes.CLICKED_FIND_MY_MATCH: {
      const { searchSupplierCategory } = payload;
      track({
        event: 'Clicked find my match',
        searchSupplierCategory,
      });
      break;
    }
    case SearchActionTypes.CHANGED_CATEGORY_SELECTION: {
      const { selectedSupplierCategory } = payload;
      track({
        event: 'Changed category selection',
        selectedSupplierCategory,
      });
      break;
    }
    case SearchActionTypes.CLICKED_SEARCH_MYSELF: {
      const { searchSupplierCategory } = payload;
      track({
        event: 'Clicked search myself',
        searchSupplierCategory,
      });
      break;
    }
    case SearchActionTypes.CLICKED_SHOW_ME_SUPPLIERS: {
      track({
        event: 'Clicked show me suppliers',
      });
      break;
    }
    case SearchActionTypes.USER_WENT_TO_A_PREVIOUS_QUIZ_PAGE: {
      const { quizName, quizStepNumber, quizStepContent } = payload;
      track({
        event: 'User went to a previous quiz page',
        quizName,
        quizStepNumber,
        quizStepContent,
      });
      break;
    }
    case SearchActionTypes.TAB_NAVIGATION_SUPPLIERS: {
      const { selectedTab } = payload as ISearchTriggerClickedSuppliersPageAnalytics['payload'];
      const state = getState();
      const userEmail = state.users.user?.contacts?.email;
      const previousPath = state.app.previousPath;
      const collaborator = getIsCollaborator(state);

      track({
        event: 'Used tab navigation on search landing page',
        category: 'Navigation',
        profileType: 'user',
        collaborator,
        previousPath,
        userEmail,
        selectedTab,
      });
      break;
    }
    case SearchActionTypes.CLICKED_REGISTER_INTEREST_BANNER_ANALYTICS: {
      const state = getState();
      const userEmail = state.users.user?.contacts?.email;
      const collaborator = getIsCollaborator(state);
      track({
        event: 'Clicked register interest on banner popup',
        ...genericProperties(state),
        userEmail,
        collaborator,
        profileType: 'user',
        viewedLocation: 'search',
        searchSupplierCategory: 'beauty',
      });
      break;
    }

    case SearchActionTypes.INTERACTED_WITH_AD_ANALYTICS: {
      const { track } = bridebookAnalytics.getMethods('advertising');
      const {
        actionType,
        adLocation,
        id: adId,
        position: adPosition,
        size: adSize,
        category: adCategory,
        supplier: adSupplierId,
      } = payload;

      track({
        event: 'Interacted with ad',
        actionType,
        adLocation,
        adId,
        adPosition,
        adSize,
        adCategory,
        adSupplierId,
      });
      break;
    }

    case SearchActionTypes.VIEWED_SUPPLIER_IN_SEARCH_ANALYTICS: {
      const { supplier, pagePosition, searchPosition } = payload;
      const viewedSupplierEvent = buildViewedSupplierInSearchEvent(
        supplier,
        getState(),
        pagePosition,
        searchPosition,
      );
      track(viewedSupplierEvent);
      break;
    }

    default:
      eventHandled = false;
      break;
  }

  if (!eventHandled) {
    const typedAction = action as
      | IChangedSearchCategoryAnalytics
      | IChangedSearchLocationAnalytics
      | IClickedOnFilteredSearchTileAnalytics;

    const state = getState();
    const searchLocation = state.search.searchLocation;
    if (searchLocation.status === 'initialized') {
      const searchTerm = searchLocation.selected.searchText;
      switch (typedAction.type) {
        case SearchActionTypes.CHANGED_SEARCH_CATEGORY_ANALYTICS:
          track({
            ...genericProperties(state),
            event: 'Changed search category',
            category: 'Supplier search',
            searchTerm,
            ...typedAction.payload,
            searchCategoryChangeSource: getSearchChangeSource(),
          });
          break;
        case SearchActionTypes.CHANGED_SEARCH_LOCATION_ANALYTICS:
          track({
            ...genericProperties(state),
            event: 'Changed search location',
            category: 'Supplier search',
            ...typedAction.payload,
            noOfLettersEntered:
              typedAction.payload.notAutocompletedSearchTerm &&
              typedAction.payload.notAutocompletedSearchTerm !== typedAction.payload.newSearchTerm
                ? typedAction.payload.notAutocompletedSearchTerm.length
                : undefined,
            searchLocationChangeSource:
              typedAction.payload.searchLocationChangeSource || getSearchChangeSource(),
          });
          break;
        case SearchActionTypes.CLICKED_ON_FILTERED_SEARCH_TILE_ANALYTICS:
          track({
            ...genericProperties(state),
            event: 'Clicked on filtered search tile',
            category: 'Supplier search',
            searchTerm,
            searchSupplierCategory:
              typedAction.payload.searchTileFilterCategory === 'supplierType'
                ? typedAction.payload.searchTileFilterValue
                : 'venue',
            ...typedAction.payload,
          });
          break;
      }
    }
  }
}

export const changedSearchLocationAnalyticsDebouncedEpic = (
  action$: Observable<
    IChangedSearchLocationAnalyticsDebounced | ISetSearchLocationAutocompleteText
  >,
  { state$ }: IEpicDeps,
) =>
  action$.pipe(
    ofType(
      SearchActionTypes.CHANGED_SEARCH_LOCATION_ANALYTICS_DEBOUNCED,
      SearchActionTypes.SET_SEARCH_LOCATION_AUTOCOMPLETE_TEXT,
    ),
    withLatestFrom(state$),
    debounceTime(DEFAULT_DEBOUNCE_TIME),
    mergeMap(([action, state]) => {
      if (action.type === SearchActionTypes.SET_SEARCH_LOCATION_AUTOCOMPLETE_TEXT) {
        const {
          payload: { searchText },
        } = action;
        const search: ISearchState = state.search;
        const searchLocation = search.searchLocation;
        if (searchLocation.status !== 'initialized' || !search.request.slug) return of();
        const category = search.request.slug;
        return of(
          changedSearchLocationAnalyticsDebounced({
            newSearchTerm: searchText,
            previousSearchTerm: searchLocation.selected.searchText,
            searchSupplierCategory: category,
          }),
        );
      } else if (action.type === SearchActionTypes.CHANGED_SEARCH_LOCATION_ANALYTICS_DEBOUNCED) {
        const payload = action.payload;
        return of(changedSearchLocationAnalytics(payload));
      } else {
        return of();
      }
    }),
  );

const getSearchChangeSource = () => {
  const pathname = Router.pathname;
  if (pathname === ProtectedUrls.home) {
    return 'home';
  } else {
    return 'searchResults';
  }
};

const genericProperties = (state: IApplicationState) => {
  const previousPath = state.app.previousPath;
  return {
    previousPath,
  };
};
